/**
 * Where a model says it came from (plan T0(d), reviewer gate 2026-08-12).
 *
 * ## Two sources, and they are not equally strong
 *
 * **The file.** GGUF carries `general.base_model.{i}.repo_url` and friends,
 * written by whatever converted it, plus `general.quantized_by`. That is
 * machine testimony recorded at conversion time: it travels with the bytes and
 * cannot be edited without rewriting the file.
 *
 * **The model card.** `cardData.base_model` in the README's frontmatter. Author
 * testimony, editable afterwards without touching a byte of weights.
 *
 * A careful uploader and a careless one write READMEs identically, so *which
 * source spoke* is a better confidence signal than who published the repo —
 * which is why every claim here carries its provenance and the UI labels it.
 * Measured on real repos: `unsloth/Qwen3.6-27B-GGUF` declares both;
 * `prism-ml/Ternary-Bonsai-27B-gguf` declares only the card.
 *
 * Safetensors and MLX have **no in-file equivalent** — their `__metadata__` is
 * `{"format":"pt"}` or `{"format":"mlx"}`, the producing framework and nothing
 * about lineage — so for those formats only the card can ever speak.
 *
 * ## Both are free
 *
 * The GGUF map is already parsed for the inventory, and `cardData` arrives in
 * the `/api/models/{repo}/revision/{rev}` response `resolveRepo` already makes.
 * No new request, no new origin, nothing to argue about against plan §5.
 *
 * ## Disagreement is displayed, never resolved
 *
 * The reviewer's two-instruments rule: the Hub's own sources contradicting each
 * other is a fact about the repo worth showing, and not ours to adjudicate
 * silently. So `agreement` is reported and both claims survive.
 *
 * ## Every value here is an author-controlled string
 *
 * `repo_url` is written by whoever made the file; `base_model` by whoever wrote
 * the README. Neither is more trustworthy for *parsing* than something typed
 * into the box — so both go through `normalizeRepoId` and `isCanonicalRepoId`,
 * the same predicates typed input meets, with no exception path because the
 * string arrived in metadata. Data is data whichever field it came in.
 *
 * A value that fails is dropped silently. A malformed declaration in someone
 * else's README is not the visitor's problem and not worth an error about.
 */

import { isCanonicalRepoId, normalizeRepoId } from "./hub.js";

/** Which testimony a claim rests on. */
export type ProvenanceSource = "file" | "card";

export interface BaseModelClaim {
  readonly repoId: string;
  /** Both, when the file and the card name the same repo. */
  readonly sources: readonly ProvenanceSource[];
}

export type Agreement =
  /** File and card name the same repo. The strongest available. */
  | "agree"
  /** Only the file spoke — a conversion tool recorded its input. */
  | "file-only"
  /** Only the card spoke. All safetensors and MLX repos are at best this. */
  | "card-only"
  /** They named different repos. Shown as-is; see the file comment. */
  | "disagree"
  | "none";

/**
 * What a GGUF file says about its own lineage, pulled out of the header map.
 *
 * A separate type because it is **stored**: `Inventory.fileProvenance` is what
 * the IndexedDB cache holds, and a cached schema needs a declared shape rather
 * than whatever the header parser currently retains.
 */
export interface FileProvenance {
  /** Raw declared values — URLs or ids, unvalidated until `readProvenance`. */
  readonly baseModels: readonly string[];
  readonly quantizedBy?: string;
}

export interface Provenance {
  readonly claims: readonly BaseModelClaim[];
  readonly agreement: Agreement;
  /** `general.quantized_by`, when the file says. The relation the card omits. */
  readonly quantizedBy?: string;
}

/**
 * A cap on how many chips a merge can put on screen.
 *
 * The reviewer asked for all of them "up to a small cap", because a merge's
 * lineage is plural and picking one is a claim. Four is where a row of chips
 * stops being a row; beyond it the honest thing is to say there are more rather
 * than to choose which.
 */
export const MAX_BASE_MODEL_CHIPS = 4;

/** A GGUF string value, or nothing. The map holds several value shapes. */
function str(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

/**
 * Repo ids the **file** declares.
 *
 * `general.base_model.count` says how many, and each is a small block of keys.
 * `repo_url` is preferred because it is unambiguous; `organization` + `name`
 * are a fallback for files that omit it, and are joined rather than trusted —
 * the result still has to survive validation like everything else.
 */
export function baseModelsFromGguf(metadata: ReadonlyMap<string, unknown>): string[] {
  const declared = metadata.get("general.base_model.count");
  const count = typeof declared === "number" ? declared : 0;
  const found: string[] = [];

  for (let i = 0; i < count && i < 64; i++) {
    const url = str(metadata.get(`general.base_model.${i}.repo_url`));
    if (url !== undefined) {
      found.push(url);
      continue;
    }
    const org = str(metadata.get(`general.base_model.${i}.organization`));
    const name = str(metadata.get(`general.base_model.${i}.name`));
    /*
     * `name` is a display name — "Qwen3.6 27B", with a space, where the repo is
     * `Qwen3.6-27B`. Joining them is a guess, so it is offered to validation
     * rather than used: a guess that survives `isCanonicalRepoId` is still only
     * a candidate, and one that does not is dropped without comment.
     */
    if (org !== undefined && name !== undefined) found.push(`${org}/${name}`);
  }
  return found;
}

/**
 * The whole file-side claim, or nothing when the file makes none.
 *
 * The single extraction point. `read-inventory.ts` calls it to fill the field
 * the cache stores, and the tests call it to build fixtures — deliberately the
 * same function, because two implementations of "what does the header say" is
 * one edit away from a cached value and a fresh read disagreeing about a model.
 */
export function fileProvenanceFromGguf(
  metadata: ReadonlyMap<string, unknown>,
): FileProvenance | undefined {
  const baseModels = baseModelsFromGguf(metadata);
  const quantizedBy = str(metadata.get("general.quantized_by"));
  if (baseModels.length === 0 && quantizedBy === undefined) return undefined;
  return { baseModels, ...(quantizedBy !== undefined ? { quantizedBy } : {}) };
}

/** Repo ids the **card** declares. `base_model` is a string or a list. */
export function baseModelsFromCard(cardData: unknown): string[] {
  if (typeof cardData !== "object" || cardData === null) return [];
  const value = (cardData as { base_model?: unknown }).base_model;
  if (typeof value === "string") return [value];
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === "string");
}

/**
 * A declared value to a repo id we are willing to navigate to, or nothing.
 *
 * Two gates, both the ones typed input meets. `normalizeRepoId` accepts a Hub
 * URL and **rejects any other host**, which is what makes `repo_url` safe to
 * read at all — the string is written by whoever made the file and can point
 * anywhere. `isCanonicalRepoId` then refuses traversal-like forms.
 */
function toRepoId(declared: string): string | undefined {
  let normalized: string;
  try {
    normalized = normalizeRepoId(declared);
  } catch {
    return undefined;
  }
  return isCanonicalRepoId(normalized) ? normalized : undefined;
}

export interface ProvenanceInput {
  /** What the file declared, when it is a GGUF and it declared anything. */
  readonly fileProvenance?: FileProvenance;
  /** `cardData` from the model API response already fetched. */
  readonly cardData?: unknown;
  /** The repo being looked at, so it cannot suggest itself. */
  readonly repoId: string;
}

export function readProvenance(input: ProvenanceInput): Provenance {
  const fromFile = new Set(
    (input.fileProvenance?.baseModels ?? [])
      .map(toRepoId)
      .filter((id): id is string => id !== undefined),
  );
  const fromCard = new Set(
    baseModelsFromCard(input.cardData)
      .map(toRepoId)
      .filter((id): id is string => id !== undefined),
  );

  /*
   * A repo declaring itself as its own base is common enough to guard — some
   * cards list the repo they were copied from, some list themselves. Comparison
   * is case-insensitive because the Hub treats owner and name that way, while
   * the declared string preserves whatever case its author typed.
   */
  const self = input.repoId.toLowerCase();
  for (const set of [fromFile, fromCard]) {
    for (const id of set) if (id.toLowerCase() === self) set.delete(id);
  }

  const claims: BaseModelClaim[] = [];
  for (const repoId of [...fromFile, ...fromCard]) {
    if (claims.some((c) => c.repoId === repoId)) continue;
    const sources: ProvenanceSource[] = [];
    if (fromFile.has(repoId)) sources.push("file");
    if (fromCard.has(repoId)) sources.push("card");
    claims.push({ repoId, sources });
  }

  return {
    claims,
    agreement: classify(fromFile, fromCard),
    ...(input.fileProvenance?.quantizedBy !== undefined
      ? { quantizedBy: input.fileProvenance.quantizedBy }
      : {}),
  };
}

function classify(file: ReadonlySet<string>, card: ReadonlySet<string>): Agreement {
  if (file.size === 0 && card.size === 0) return "none";
  if (file.size === 0) return "card-only";
  if (card.size === 0) return "file-only";
  const shared = [...file].some((id) => card.has(id));
  /*
   * Any overlap counts as agreement. A merge whose card lists three parents and
   * whose file records one is not two instruments contradicting each other —
   * it is one of them being less complete, which is a different and much less
   * interesting thing than naming different models.
   */
  return shared ? "agree" : "disagree";
}
